// Labor (Clock In/Out) — IC-5893. Thin client for routes/LaborRoutes.js.
// Every labor route answers {success, ...}; a failure carries an error code (e.g. 'forbidden',
// 'day_has_pending_edit') that laborErrorText() turns into a message.
import {fetchPostAuthSafe} from '../../app/utils';

class LaborApiError extends Error {
  constructor(code, status, detail) {
    super(code);
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

async function readJson(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    // A lapsed session answers a GET with the login page, not JSON
    throw new LaborApiError(response.redirected ? 'no_session' : 'server_error', response.status);
  }
  if (!response.ok || !body || body.success === false) {
    throw new LaborApiError((body && body.error) || 'server_error', response.status, body && body.detail);
  }
  return body;
}

function queryString(params) {
  const qs = new URLSearchParams();
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? '?' + s : '';
}

async function laborGet(path, params) {
  const response = await fetch('/labor/' + path + queryString(params), {credentials: 'same-origin'});
  return readJson(response);
}

async function laborPost(path, body, userSettings) {
  const response = await fetchPostAuthSafe(
    '/labor/' + path,
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body || {}),
    },
    userSettings.username,
    userSettings.databaseName
  );
  return readJson(response);
}

function downloadExportUrl(exportId) {
  return '/labor/downloadExport' + queryString({id: exportId});
}

// Fetch the stored export and hand it to the browser as a file. Going through fetch (not a plain
// link) keeps the request on the API path: the dev server proxies it, and a refusal (404, 403, a
// lapsed session) comes back as a LaborApiError instead of Chrome's bare "File wasn't available".
async function downloadExport(exportId, filename) {
  const response = await fetch(downloadExportUrl(exportId), {credentials: 'same-origin'});
  const type = response.headers.get('Content-Type') || '';
  if (!response.ok || !type.startsWith('text/csv')) {
    await readJson(response); // throws the server's error code
    throw new LaborApiError('server_error', response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'labor-export.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
}

// A translated message for a LaborApiError (or anything thrown), falling back to a generic one.
function laborErrorText(t, err) {
  const code = (err && err.code) || 'server_error';
  const key = 'labor.error.' + code;
  const text = t(key);
  return text === key ? t('labor.error.server_error') : text;
}

export {LaborApiError, laborGet, laborPost, downloadExportUrl, downloadExport, laborErrorText};
